import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  Avatar,
  LinearProgress,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  TrendingUp,
  AccountBalanceWallet,
  Assignment,
  Star,
  PersonAdd,
  Settings
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import LoadFundings from '../../request/Funding/LoadFundings';
import P2PPartnerApplication from './P2PPartnerApplication';
import P2PPartnerProfile from './P2PPartnerProfile';
import P2PRateManagement from './P2PRateManagement';
import P2POrderManagement from './P2POrderManagement';
import P2PPartnerStats from './P2PPartnerStats';

const P2PPartnerDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  // Get partner data from user Redux store
  const userData = useSelector((state) => state.userData?.user);
  const loading = useSelector((state) => state.userData?.loading);
  
  const partnerData = userData?.p2p_partner;
  const statistics = partnerData?.statistics;
  const partnerOrders = userData?.p2p_partner_orders || [];

  // Debug logging
  console.log('P2PPartnerDashboard Debug:', {
    partnerData,
    statistics,
    partnerOrders,
    loading
  });

  // Timeout to force loading to false for testing
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('Force loading to false after 5 seconds');
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const fetchPartnerProfile = () => {
    // Data will be loaded by LoadP2PPartner component in App.js
    // This is just a placeholder for the onSuccess callback
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'pending':
        return 'Under Review';
      case 'rejected':
        return 'Rejected';
      case 'suspended':
        return 'Suspended';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  // If no partner profile exists, show application form
  if (!partnerData) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <P2PPartnerApplication onSuccess={fetchPartnerProfile} />
        </motion.div>
      </Container>
    );
  }

  // If partner profile exists but is not approved, show status message
  if (partnerData && partnerData.status !== 'approved') {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" gutterBottom>
                P2P Partner Application Status
              </Typography>
              <Chip
                label={getStatusText(partnerData.status)}
                color={getStatusColor(partnerData.status)}
                size="large"
                sx={{ fontSize: '1.1rem', py: 2, px: 3 }}
              />
            </Box>

            {partnerData.status === 'pending' && (
              <Box>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Your application is under review
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Thank you for applying to become a P2P partner. Our team is reviewing your application 
                  and will notify you once it's approved. You'll be able to create rates and start trading once approved.
                </Typography>
              </Box>
            )}

            {partnerData.status === 'rejected' && (
              <Box>
                <Typography variant="h6" color="error.main" gutterBottom>
                  Application Rejected
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Unfortunately, your P2P partner application was not approved.
                </Typography>
                {partnerData.rejection_reason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                    Reason: {partnerData.rejection_reason}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => window.location.reload()}
                  sx={{ mt: 2 }}
                >
                  Apply Again
                </Button>
              </Box>
            )}

            {partnerData.status === 'suspended' && (
              <Box>
                <Typography variant="h6" color="error.main" gutterBottom>
                  Account Suspended
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Your P2P partner account has been temporarily suspended.
                </Typography>
                {partnerData.suspension_reason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                    Reason: {partnerData.suspension_reason}
                  </Typography>
                )}
                {partnerData.suspended_until && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Suspended until: {new Date(partnerData.suspended_until).toLocaleDateString()}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Please contact support if you believe this is an error.
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Applied on: {new Date(partnerData.created_at).toLocaleDateString()}
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <LoadFundings />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}
                >
                  {partnerData.partner_name.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {partnerData.partner_name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Chip
                      label={getStatusText(partnerData.status)}
                      color={getStatusColor(partnerData.status)}
                      size="small"
                    />
                    {partnerData.is_verified && (
                      <Chip
                        icon={<Star />}
                        label="Verified"
                        color="success"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Settings />}
                  onClick={() => setActiveTab(1)}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Settings
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Statistics Cards */}
        {statistics && (
          <Box sx={{ mb: 4 }}>
            <P2PPartnerStats statistics={statistics} />
          </Box>
        )}

        {/* Main Content */}
        <Paper sx={{ width: '100%', bgcolor: 'background.paper' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab
                label="Overview"
                icon={<TrendingUp />}
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
              <Tab
                label="Profile"
                icon={<PersonAdd />}
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
              <Tab
                label="Rates"
                icon={<AccountBalanceWallet />}
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
              <Tab
                label="Orders"
                icon={<Assignment />}
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {activeTab === 0 && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <P2POrderManagement partnerId={partnerData.id} />
              </motion.div>
            )}

            {activeTab === 1 && (
              <motion.div
                key="profile"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <P2PPartnerProfile
                  partnerData={partnerData}
                  onUpdate={fetchPartnerProfile}
                />
              </motion.div>
            )}

            {activeTab === 2 && (
              <motion.div
                key="rates"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <P2PRateManagement partnerId={partnerData.id} />
              </motion.div>
            )}

            {activeTab === 3 && (
              <motion.div
                key="orders"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <P2POrderManagement partnerId={partnerData.id} />
              </motion.div>
            )}
          </Box>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default P2PPartnerDashboard;
